import React, { useContext, useState, useEffect, useCallback } from "react";
import {
  Card,
  Spinner,
  Table,
  Button,
  Label,
  TextInput,
  Modal,
  Pagination,
  Select,
  Datepicker,
} from "flowbite-react";
import { Store } from "../context/store";
import { IoMdArrowBack, IoMdAddCircleOutline, IoMdSend } from "react-icons/io";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiRequest from "../utils/apiRequest";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { AiOutlineClear } from "react-icons/ai";
import { useAllPermissions } from "../utils/usePermission";
import { confirmDialog } from "../components/ConfirmDialog";
import DashboardLayout from "../components/layout/DashboardLayout";

function Vouchers() {
  let store = useContext(Store);
  let [baseUrl] = store.url;
  let [voucherArray, setVoucherArray] = store.voucherArray;
  let [, setLoading] = useState(false);
  const [secondCard, setSecondCard] = useState(false);
  const [invoiceBody, setInvoiceBody] = useState({});
  const navigate = useNavigate();
  let [msg, setMsg] = useState("");
  const [openModalOut, setOpenModalOut] = useState(false);
  let [expenditures, setExpenditures] = useState([]);
  let [loading1] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [totalPages, setTotalPages] = useState(1);
  const page = parseInt(searchParams.get("page")) || 1;
  const [expenditureType, setExpenditureType] = useState(
    searchParams.get("type") || "",
  );
  const [currentPage, setCurrentPage] = useState(page);
  const [openModal, setOpenModal] = useState(false);
  const [error, setError] = useState("");
  const [selectedExpenditure, setSelectedExpenditure] = useState({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateField, setDateField] = useState("valueDate");

  const { canWriteVouchers } = useAllPermissions();

  function onCloseModal() {
    setOpenModal(false);
  }

  function viewExpenditure(expenditure) {
    setSelectedExpenditure(expenditure);
    setOpenModal(true);
  }

  const onPageChange = (page) => {
    const nextParams = { page: String(page) };
    if (expenditureType) nextParams.type = expenditureType;
    setSearchParams(nextParams);
    setCurrentPage(page);
  };

  const onTypeChange = (event) => {
    const nextType = event.target.value;
    setExpenditureType(nextType);
    setCurrentPage(1);
    setSearchParams(nextType ? { page: "1", type: nextType } : { page: "1" });
  };

  const loadExpenditures = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("royal-token");

      const params = new URLSearchParams();
      params.set("page", String(page));
      if (expenditureType) params.set("type", expenditureType);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (startDate || endDate) params.append("dateField", dateField);

      const url = `${baseUrl}/approved/expenditures?${params.toString()}`;
      const { data } = await apiRequest(url, "GET", null, token);
      const expenditureList = data?.expenditures || [];

      // Keep the table safe even if the API unexpectedly returns other statuses.
      setExpenditures(
        expenditureList.filter(
          (expenditure) => expenditure?.status?.toLowerCase() === "approved",
        ),
      );
      setTotalPages(Math.max(data?.totalPages || 1, 1));
    } catch (error) {
      const unauthorizedErrors = [
        "Access Denied: Invalid or expired token. Please login again",
        "Access Denied: Please log in.",
        "Access Denied: Invalid Token.",
        "Access Denied: Invalid or expired token.",
        "Access Denied: Invalid or Expired Token.",
        "Access Denied: No token provided.",
      ];
      if (unauthorizedErrors.includes(error.message)) {
        setMsg("Session Expired!");
        setOpenModalOut(true);
        localStorage.removeItem("royal-token");
      } else {
        console.log(error);
      }
    } finally {
      setLoading(false);
    }
  }, [baseUrl, page, expenditureType, startDate, endDate, dateField]);

  const pushToInvoice = (e) => {
    const validations = [
      { condition: !e.id, message: "Please select an expenditure!" },
      {
        condition: e.status?.toLowerCase() !== "approved",
        message: "Only approved expenditures can be added to a voucher.",
      },
      { condition: !e.payee, message: "Please enter a payee!" },
      { condition: !e.total, message: "Please enter an amount!" },
      { condition: !e.paymentType, message: "Please select a payment type!" },
    ];

    for (const validation of validations) {
      if (validation.condition) {
        setError(validation.message);
        return false;
      }
    }
    voucherArray.rows.push({
      expenditureId: e.id,
      payee: e.payee,
      total: e.total,
      accountNumber: e.accountNumber,
      bank: e.bank,
      paymentType: e.paymentType,
    });
    setError("");
    setInvoiceBody({
      expenditureId: "",
      payee: "",
      total: "",
      accountNumber: "",
      bank: "",
      paymentType: "",
    });
  };

  const createVoucher = async () => {
    if (voucherArray.rows.length === 0) {
      setError("At least one expenditure must be added to the list.");
      return;
    }

    await confirmDialog(
      "Are you sure you want to create these Voucher Request(s)?",
      {
        title: "Create Voucher Requests",
        confirmText: "Yes, create",
        color: "success",
        loadingText: "Creating...",
        onConfirm: async () => {
          setLoading(true);
          try {
            let token = localStorage.getItem("royal-token");
            const url = `${baseUrl}/voucher`;

            // Loop through each row and send a separate request
            for (const item of voucherArray.rows) {
              const body = { item: [item] }; // Send each as an array with one item
              await apiRequest(url, "POST", body, token);
            }

            setError("Voucher request(s) created successfully!");
            setVoucherArray({ rows: [] }); // Reset rows
            setInvoiceBody({
              expenditureId: "",
              payee: "",
              total: "",
              accountNumber: "",
              bank: "",
              paymentType: "",
            }); // Reset form
            loadExpenditures(); // Reload expenditures
          } catch (error) {
            setError(error.message || "Something went wrong.");
            console.log(error);
          } finally {
            setLoading(false);
          }
        },
      },
    );
  };

  let logOut = () => {
    navigate("/");
  };

  useEffect(() => {
    Promise.all([loadExpenditures()])
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, [loadExpenditures]);

  return (
    <>
      <title>Vouchers</title>
      <meta
        name="description"
        content="Vouchers Page to the Royalmines Property Office"
      />
      <DashboardLayout active="vouchers">
        {!secondCard && (
          <div className="col-span-12 md:col-span-4 mx-2" data-aos="flip-up">
            <div className="mb-4">
              <h5 className="text-xl font-bold leading-none text-gray-900 dark:text-white">
                Approved Expenditures
              </h5>
              <div className="flex flex-wrap gap-2 mt-5 justify-end">
                <Select
                  aria-label="Filter expenditures by type"
                  value={expenditureType}
                  onChange={onTypeChange}
                  className="min-w-40"
                >
                  <option value="">All types</option>
                  <option value="OpEx">OpEx</option>
                  <option value="CapEx">CapEx</option>
                </Select>
                {/* <Button
                  color="success"
                  onClick={() => setSecondCard(true)}
                  disabled={!canWriteVouchers}
                >
                  <IoMdAddCircleOutline className="me-2" />
                  New Vouchers
                </Button> */}
              </div>
            </div>
            {!loading1 ? (
              <>
                <Card className="rounded-xl my-4">
                  <div className="overflow-x-auto overflow-y-scroll h-[50vh] scrollbar-always-visible">
                    <Table
                      striped
                      hoverable
                      className="w-full text-sm text-left text-gray-500 uppercase"
                    >
                      <thead className="bg-gray-100 sticky top-0 z-5">
                        <tr>
                          <th className="px-6 py-3">Initiated By</th>
                          <th className="px-6 py-3">Purpose</th>
                          <th className="px-6 py-3">Type</th>
                          <th className="px-6 py-3">Total</th>
                          <th className="px-6 py-3">Action</th>
                        </tr>
                      </thead>

                      <Table.Body className="divide-y">
                        {expenditures.length === 0 ? (
                          <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800">
                            <Table.Cell
                              colSpan={5}
                              className="text-center py-10"
                            >
                              No approved expenditures.
                            </Table.Cell>
                          </Table.Row>
                        ) : (
                          expenditures.map((e, i) => (
                            <Table.Row
                              key={e.id || i}
                              className="bg-white dark:border-gray-700 dark:bg-gray-800 read-msgs"
                            >
                              <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white align-top">
                                {e.requestedBy.firstName}{" "}
                                {e.requestedBy.lastName}
                              </Table.Cell>
                              <Table.Cell className="text-ellipsis align-top">
                                {e.purpose?.map((item, index) => (
                                  <div key={index} className="mb-1">
                                    <p>{item.item}</p>
                                    <p className="normal-case text-gray-500">
                                      {item.quantity} × &#8358;
                                      {item.price?.toLocaleString()}
                                    </p>
                                  </div>
                                ))}
                              </Table.Cell>
                              <Table.Cell className="align-top">
                                {e.type}
                              </Table.Cell>
                              <Table.Cell className="text-ellipsis align-top">
                                &#8358; {e.total?.toLocaleString()}
                              </Table.Cell>
                              <Table.Cell className="align-top">
                                <Button
                                  size="xs"
                                  color="light"
                                  onClick={() => viewExpenditure(e)}
                                >
                                  View
                                </Button>
                              </Table.Cell>
                            </Table.Row>
                          ))
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                </Card>

                {expenditures.length > 0 && (
                  <div className="flex overflow-x-auto sm:justify-center mb-5">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={onPageChange}
                      showIcons
                      previousLabel="Previous"
                      nextLabel="Next"
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="flex justify-center items-center h-[100%]">
                <Spinner
                  style={{ color: "#0078e7" }}
                  aria-label="info spinner example"
                  size="xl"
                />
              </div>
            )}
          </div>
        )}

        {/* add items to vouchers */}
        {secondCard && (
          <div className="col-span-5 md:col-span-4" data-aos="slide-right">
            <h5 className="text-xl font-bold leading-none text-gray-900 dark:text-white">
              Vouchers
            </h5>
            <div className="flex flex-wrap gap-2 mt-5 justify-between">
              <Button
                onClick={() => {
                  setSecondCard(false);
                  setError("");
                  setStartDate("");
                  setEndDate("");
                  setDateField("valueDate");
                }}
                className="bg-#6eb5ff"
              >
                <IoMdArrowBack className="me-2" />
                Go Back
              </Button>

              <Button className="bg-#6eb5ff" onClick={() => createVoucher()}>
                Submit
                <IoMdSend className="ms-2" />
              </Button>
            </div>

            <p className="text-red-600 text-center my-3 font-bold">{error}</p>
            <Card className="w-1/2 mx-auto">
              <div className="flex flex-col gap-4">
                {/* item */}
                <div>
                  <div className="p-4 bg-slate-50/50 rounded-lg border border-slate-200">
                    <p>Filter Expenditures by Date (optional)</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ">
                      <div className="space-y-2">
                        <Label
                          htmlFor="startDate"
                          className="text-sm font-medium text-slate-700"
                        >
                          Start Date
                        </Label>
                        <Datepicker
                          id="startDate"
                          value={startDate}
                          onSelectedDateChanged={(date) => {
                            // If it's a Date object, format it as yyyy-mm-dd
                            if (date instanceof Date) {
                              const year = date.getFullYear();
                              const month = String(
                                date.getMonth() + 1,
                              ).padStart(2, "0");
                              const day = String(date.getDate()).padStart(
                                2,
                                "0",
                              );
                              setStartDate(`${year}-${month}-${day}`);
                            }
                          }}
                          showClearButton={false}
                          showTodayButton={false}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="endDate"
                          className="text-sm font-medium text-slate-700"
                        >
                          End Date
                        </Label>
                        <Datepicker
                          id="endDate"
                          value={endDate}
                          onSelectedDateChanged={(date) => {
                            // If it's a Date object, format it as yyyy-mm-dd
                            if (date instanceof Date) {
                              const year = date.getFullYear();
                              const month = String(
                                date.getMonth() + 1,
                              ).padStart(2, "0");
                              const day = String(date.getDate()).padStart(
                                2,
                                "0",
                              );
                              setEndDate(`${year}-${month}-${day}`);
                            }
                          }}
                          showClearButton={false}
                          showTodayButton={false}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="voucherDateField"
                          className="text-sm font-medium text-slate-700"
                        >
                          Date Field
                        </Label>
                        <Select
                          id="voucherDateField"
                          value={dateField}
                          onChange={(e) => setDateField(e.target.value)}
                        >
                          <option value="valueDate">Value Date</option>
                          <option value="createdAt">Transaction Date</option>
                        </Select>
                      </div>
                    </div>
                    <Button
                      color="light"
                      size="sm"
                      className="mt-4"
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                        setDateField("valueDate");
                      }}
                    >
                      Clear Date Filters
                    </Button>
                  </div>

                  <div className="mb-2">
                    <div className="mb-2 block">
                      <Label htmlFor="expenditureId" value="Expenditure" />
                    </div>
                    <Select
                      value={JSON.stringify(invoiceBody)}
                      onChange={(e) =>
                        setInvoiceBody(JSON.parse(e.target.value))
                      }
                      id="expenditureId"
                      required
                    >
                      <option value="{}">Select an approved expenditure</option>
                      {expenditures?.map((e, i) => (
                        <option key={e.id || i} value={JSON.stringify(e)}>
                          {e.requestedBy.firstName} {e.requestedBy.lastName}{" "}
                          requested {e.purpose[0]?.item} ({e.type}) - ₦
                          {e.total?.toLocaleString()}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div className="mb-2">
                    <div className="mb-2 block">
                      <Label htmlFor="payee" value="Payee" />
                    </div>
                    <TextInput
                      id="payee"
                      type="text"
                      placeholder="Enter Payee"
                      value={invoiceBody.payee}
                      disabled
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div className="col-span-1">
                      <div className="mb-2 block">
                        <Label htmlFor="Amount11" value="Amount" />
                      </div>
                      <TextInput
                        id="Amount11"
                        type="number"
                        step="0.01"
                        placeholder="Enter Amount"
                        min={0}
                        value={invoiceBody.total}
                        disabled
                      />
                    </div>

                    <div className="col-span-1">
                      <div className="mb-2 block">
                        <Label htmlFor="Account1" value="Account Number" />
                      </div>
                      <TextInput
                        id="Account1"
                        type="text"
                        placeholder="Enter Account Number"
                        min={0}
                        value={invoiceBody.accountNumber}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div className="col-span-1">
                      <div className="mb-2 block">
                        <Label htmlFor="Bank1" value="Bank" />
                      </div>
                      <TextInput
                        id="Bank1"
                        type="text"
                        placeholder="Enter Bank Name"
                        min={0}
                        value={invoiceBody.bank}
                        disabled
                      />
                    </div>

                    <div className="col-span-1">
                      <div className="mb-2 block">
                        <Label htmlFor="paymentType" value="Payment Type" />
                      </div>
                      <Select
                        value={invoiceBody.paymentType}
                        id="paymentType"
                        disabled
                        required
                      >
                        <option value="">Select Payment Type</option>
                        <option value="Transfer">Transfer</option>
                        <option value="Cash">Cash</option>
                        <option value="POS">POS</option>
                        <option value="Cheque">Cheque</option>
                      </Select>
                    </div>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={() => pushToInvoice(invoiceBody)}
                  className="bg-#6eb5ff"
                >
                  Add to List
                </Button>
              </div>
            </Card>
          </div>
        )}

        {secondCard && (
          <>
            <div className="flex flex-wrap gap-2 mt-5 justify-end">
              <Button
                onClick={() => {
                  setVoucherArray({ rows: [] });
                  setError("");
                }}
                className="bg-#6eb5ff"
              >
                Clear
                <AiOutlineClear className="ms-2" />
              </Button>
            </div>
            <Card className="rounded-xl my-4">
              <div className="">
                <div className="overflow-x-auto">
                  <Table className="border-2 border-black text-xs">
                    <Table.Head>
                      <Table.HeadCell colSpan={4}>
                        Expenditure ID
                      </Table.HeadCell>
                      <Table.HeadCell colSpan={1}>Amount</Table.HeadCell>
                      <Table.HeadCell colSpan={1}>Payment Type</Table.HeadCell>
                      <Table.HeadCell colSpan={1}>Payee</Table.HeadCell>
                    </Table.Head>
                    {voucherArray?.rows?.map((e, i) => {
                      return (
                        <Table.Body
                          key={e.expenditureId || i}
                          className="divide-y divide-x text-black"
                        >
                          <Table.Row>
                            <Table.Cell
                              className="whitespace-nowrap text-ellipsis"
                              colSpan={4}
                            >
                              {e.expenditureId}
                            </Table.Cell>
                            <Table.Cell colSpan={1}>
                              {e.total?.toLocaleString()}
                            </Table.Cell>
                            <Table.Cell colSpan={1}>{e.paymentType}</Table.Cell>
                            <Table.Cell colSpan={1}>{e.payee}</Table.Cell>
                          </Table.Row>
                        </Table.Body>
                      );
                    })}
                  </Table>
                </div>
              </div>
            </Card>
          </>
        )}

        {/* approved expenditure details */}
        <Modal
          dismissible
          show={openModal}
          size="lg"
          onClose={onCloseModal}
          popup
          theme={{
            root: {
              base: "fixed inset-0 z-[2000] h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
            },
            content: {
              inner:
                "relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col max-h-full",
            },
            backdrop:
              "fixed inset-0 z-[1990] bg-gray-900/50 dark:bg-gray-900/80",
          }}
        >
          <Modal.Header>Expenditure Details</Modal.Header>
          <Modal.Body>
            <div className="space-y-4 text-sm text-gray-700 dark:text-gray-200">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* <p><strong>ID:</strong> {selectedExpenditure.id || "N/A"}</p> */}
                <p>
                  <strong>Type:</strong> {selectedExpenditure.type || "N/A"}
                </p>
                <p>
                  <strong>Requested By:</strong>{" "}
                  {selectedExpenditure.requestedBy?.firstName}{" "}
                  {selectedExpenditure.requestedBy?.lastName}
                </p>
                <p>
                  <strong>Total:</strong> &#8358;
                  {selectedExpenditure.total?.toLocaleString()}
                </p>
                <p>
                  <strong>Payee:</strong> {selectedExpenditure.payee || "N/A"}
                </p>
                <p>
                  <strong>Payment Type:</strong>{" "}
                  {selectedExpenditure.paymentType || "N/A"}
                </p>
                <p>
                  <strong>Bank:</strong> {selectedExpenditure.bank || "N/A"}
                </p>
                <p>
                  <strong>Account Number:</strong>{" "}
                  {selectedExpenditure.accountNumber || "N/A"}
                </p>
                <p>
                  <strong>Value Date:</strong>{" "}
                  {formatDate(selectedExpenditure.valueDate)}
                </p>
                <p>
                  <strong>Created:</strong>{" "}
                  {formatDate(selectedExpenditure.createdAt)}
                </p>
                <p>
                  <strong>Authorized By:</strong>{" "}
                  {`${selectedExpenditure.authorizedBy?.firstName || ""} ${selectedExpenditure.authorizedBy?.lastName || ""}`.trim() ||
                    "N/A"}
                </p>{" "}
                <p>
                  <strong>Authorized Date:</strong>{" "}
                  {formatDate(selectedExpenditure.authorizedDate)}
                </p>
              </div>

              <div>
                <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Purpose
                </h4>
                <div className="space-y-2">
                  {selectedExpenditure.purpose?.map((item, index) => (
                    <div
                      key={item._id || index}
                      className="rounded-lg bg-gray-100 p-3 dark:bg-gray-600"
                    >
                      <p className="font-medium">{item.item}</p>
                      <p>Category: {item.category || "N/A"}</p>
                      <p>Quantity: {item.quantity?.toLocaleString() || 0}</p>
                      <p>Unit price: &#8358;{item.price?.toLocaleString()}</p>
                      <p>
                        Line total: &#8358;
                        {(
                          (item.price || 0) * (item.quantity || 0)
                        ).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* modal for logout */}
        <Modal
          show={openModalOut}
          size="md"
          popup
          theme={{
            root: {
              base: "fixed inset-0 z-[2000] h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
            },
            content: {
              inner:
                "relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col max-h-full",
            },
            backdrop:
              "fixed inset-0 z-[1990] bg-gray-900/50 dark:bg-gray-900/80",
          }}
        >
          <Modal.Header />
          <Modal.Body>
            <div className="text-center">
              <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
              <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                {msg}
              </h3>
              <div className="flex justify-center gap-4">
                <Button color="failure" onClick={() => logOut()}>
                  {"OK"}
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </DashboardLayout>
    </>
  );
}

export default Vouchers;

function formatDate(dateString) {
  if (!dateString) return "N/A";
  const dateObj = new Date(dateString);
  if (Number.isNaN(dateObj.getTime())) return "N/A";
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[dateObj.getMonth()];

  const desiredFormat = `${dateObj.getFullYear()}-${month}-${dateObj.getDate()}`;

  return desiredFormat;
}
